<div class="container-fluid">
  <!-- Header -->
  <div class="row mb-4">
    <div class="col">
      <h3>Workflow Migration</h3>
      <p class="text-muted">
        Manage workflow migrations and track workflow migration progress.
      </p>
    </div>
  </div>

  <!-- Jurisdiction Selector (only if no client context) -->
  <div *ngIf="!hasClientContext" class="row mb-4">
    <div class="col-md-6">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Select Jurisdiction</h5>
        </div>
        <div class="card-body">
          <label for="jurisdictionSelect" class="form-label">
            <strong>Jurisdiction</strong>
            <span class="text-danger">*</span>
          </label>
          <select
            id="jurisdictionSelect"
            class="form-control"
            [(ngModel)]="selectedJurisdiction"
            (ngModelChange)="onJurisdictionSelected($event)"
          >
            <option [ngValue]="null">-- Select a Jurisdiction --</option>
            <option
              *ngFor="let jurisdiction of jurisdictions"
              [ngValue]="jurisdiction.id"
            >
              {{ jurisdiction.name }}
            </option>
          </select>
          <small class="form-text text-muted">
            Select a jurisdiction to view and manage workflow migrations.
          </small>
        </div>
      </div>
    </div>
  </div>

  <!-- Workflow Migrations List -->
  <div
    *ngIf="selectedJurisdiction && !selectedMigrationProcessing"
    class="row mb-4"
  >
    <div class="col">
      <div class="card">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <h5 class="mb-0">Ready to Start Migrations</h5>
          <button
            class="btn btn-primary btn-sm"
            (click)="createNewMigrationProcessing()"
          >
            <i class="fa fa-plus"></i>
            Create New
          </button>
        </div>
        <div class="card-body">
          <app-loader *ngIf="loading"></app-loader>

          <div
            *ngIf="!loading && migrationProcessings.length === 0"
            class="text-center text-muted py-4"
          >
            <i class="fa fa-inbox fa-3x mb-3"></i>
            <p>No workflow migrations ready to start for this jurisdiction.</p>
            <p class="text-muted small">
              Workflow migrations that are ready to start will appear here.
            </p>
            <button
              class="btn btn-primary mt-2"
              (click)="createNewMigrationProcessing()"
            >
              <i class="fa fa-plus"></i>
              Create New Workflow Migration
            </button>
          </div>

          <div
            *ngIf="!loading && migrationProcessings.length > 0"
            class="table-responsive"
          >
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Source System</th>
                  <th>Jurisdiction</th>
                  <th>Owner</th>
                  <th>Created By</th>
                  <th>Created On</th>
                  <th>Started On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let processing of migrationProcessings"
                  style="cursor: pointer;"
                >
                  <td (click)="selectMigrationProcessing(processing)">
                    {{ processing.sourceSystem }}
                  </td>
                  <td (click)="selectMigrationProcessing(processing)">
                    {{ processing.jurisdiction }}
                  </td>
                  <td (click)="selectMigrationProcessing(processing)">
                    {{ processing.currentOwner?.name || 'Unassigned' }}
                  </td>
                  <td (click)="selectMigrationProcessing(processing)">
                    {{ processing.createdBy }}
                  </td>
                  <td (click)="selectMigrationProcessing(processing)">
                    {{ processing.createdOn | date: 'short' }}
                  </td>
                  <td (click)="selectMigrationProcessing(processing)">
                    <span *ngIf="processing.startedOn">{{
                      processing.startedOn | date: 'short'
                    }}</span>
                    <span *ngIf="!processing.startedOn" class="text-muted"
                      >Not Started</span
                    >
                  </td>
                  <td>
                    <div class="btn-group" role="group">
                      <button
                        class="btn btn-sm btn-primary"
                        (click)="selectMigrationProcessing(processing)"
                        title="View Details"
                      >
                        <i class="fa fa-arrow-right"></i>
                        Open
                      </button>
                      <button
                        class="btn btn-sm btn-danger"
                        (click)="
                          deleteMigrationProcessingById(processing.id, $event)
                        "
                        title="Delete"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Migration Processing Detail View -->
  <div
    *ngIf="selectedMigrationProcessing && !showMigrationForm"
    class="row mb-4"
  >
    <div class="col">
      <!-- Header with back button -->
      <div class="mb-3">
        <button class="btn btn-outline-secondary" (click)="backToList()">
          <i class="fa fa-arrow-left me-2"></i>
          Back to List
        </button>
      </div>

      <!-- Processing Info Card -->
      <div class="card mb-4">
        <div class="card-header bg-primary text-white">
          <h5 class="mb-0">
            <i class="fa fa-clipboard me-2"></i>
            Workflow Migration: {{ selectedMigrationProcessing.sourceSystem }}
          </h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <dl class="row">
                <dt class="col-sm-4">Source System:</dt>
                <dd class="col-sm-8">
                  {{ selectedMigrationProcessing.sourceSystem }}
                </dd>

                <dt class="col-sm-4">Jurisdiction:</dt>
                <dd class="col-sm-8">
                  {{ selectedMigrationProcessing.jurisdiction }}
                </dd>

                <dt class="col-sm-4">Created By:</dt>
                <dd class="col-sm-8">
                  {{ selectedMigrationProcessing.createdBy }}
                </dd>

                <dt class="col-sm-4">Created On:</dt>
                <dd class="col-sm-8">
                  {{ selectedMigrationProcessing.createdOn | date: 'short' }}
                </dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row">
                <dt class="col-sm-4">Current Owner:</dt>
                <dd class="col-sm-8">
                  <div class="d-flex align-items-center">
                    <span class="me-2">{{
                      selectedMigrationProcessing.currentOwner?.name ||
                        'Unassigned'
                    }}</span>
                    <button
                      class="btn btn-sm btn-outline-primary"
                      (click)="showChangeOwnerDialog = true"
                      title="Change Owner"
                    >
                      <i class="fa fa-edit"></i>
                      Change
                    </button>
                  </div>
                </dd>

                <dt class="col-sm-4">Started By:</dt>
                <dd class="col-sm-8">
                  {{ selectedMigrationProcessing.startedBy || 'Not Started' }}
                </dd>

                <dt class="col-sm-4">Started On:</dt>
                <dd class="col-sm-8">
                  <span *ngIf="selectedMigrationProcessing.startedOn">
                    {{ selectedMigrationProcessing.startedOn | date: 'short' }}
                  </span>
                  <span
                    *ngIf="!selectedMigrationProcessing.startedOn"
                    class="text-muted"
                    >Not Started</span
                  >
                </dd>

                <dt class="col-sm-4">Migrated Workflow By:</dt>
                <dd class="col-sm-8">
                  {{
                    selectedMigrationProcessing.workflowMigratedBy ||
                      'Not Migrated'
                  }}
                </dd>

                <dt class="col-sm-4">Migrated Workflow On:</dt>
                <dd class="col-sm-8">
                  <span *ngIf="selectedMigrationProcessing.workflowMigratedOn">
                    {{
                      selectedMigrationProcessing.workflowMigratedOn
                        | date: 'short'
                    }}
                  </span>
                  <span
                    *ngIf="!selectedMigrationProcessing.workflowMigratedOn"
                    class="text-muted"
                    >Not Migrated</span
                  >
                </dd>

                <dt class="col-sm-4">Migrated Workflow:</dt>
                <dd class="col-sm-8">
                  <span *ngIf="selectedMigrationProcessing.migratedWorkflowId">
                    <a
                      [routerLink]="
                        hasClientContext
                          ? [
                              '/admin/workflow-builder',
                              selectedMigrationProcessing.migratedWorkflowId
                            ]
                          : [
                              '/workflow',
                              selectedMigrationProcessing.migratedWorkflowId
                            ]
                      "
                      target="_blank"
                      class="btn btn-sm btn-outline-primary"
                      title="View Workflow"
                    >
                      <i class="fa fa-external-link me-1"></i>
                      View Workflow
                    </a>
                  </span>
                  <span
                    *ngIf="!selectedMigrationProcessing.migratedWorkflowId"
                    class="text-muted"
                  >
                    <i class="fa fa-minus-circle me-1"></i>
                    No Workflow
                  </span>
                </dd>

                <dt class="col-sm-4">Migration JSON:</dt>
                <dd class="col-sm-8">
                  <button
                    *ngIf="selectedMigrationProcessing.metaDataFilePath"
                    class="btn btn-sm btn-outline-secondary"
                    (click)="downloadMetadataFile()"
                    title="Download the JSON metadata file used for this migration"
                  >
                    <i class="fa fa-download me-1"></i>
                    Download JSON
                  </button>
                  <span
                    *ngIf="!selectedMigrationProcessing.metaDataFilePath"
                    class="text-muted"
                  >
                    <i class="fa fa-minus-circle me-1"></i>
                    No File
                  </span>
                </dd>

                <dt class="col-sm-4">Application Data:</dt>
                <dd class="col-sm-8">
                  <span
                    *ngIf="selectedMigrationProcessing.applicationDataFilePath"
                  >
                    <i class="fa fa-check-circle text-success me-1"></i>
                    File Uploaded
                  </span>
                  <span
                    *ngIf="!selectedMigrationProcessing.applicationDataFilePath"
                    class="text-muted"
                  >
                    <i class="fa fa-minus-circle me-1"></i>
                    No File
                  </span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Workflow Type and Renewal Details Card -->
      <div
        class="card mb-4"
        *ngIf="
          selectedMigrationProcessing.workflowType ||
          selectedMigrationProcessing.expirationFormula
        "
      >
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-cog me-2"></i>
            Workflow Configuration
          </h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <dl class="row">
                <dt class="col-sm-4">Workflow Type:</dt>
                <dd class="col-sm-8">
                  <span
                    *ngIf="selectedMigrationProcessing.workflowType"
                    class="badge bg-primary text-white"
                  >
                    {{ selectedMigrationProcessing.workflowType }}
                  </span>
                  <span
                    *ngIf="!selectedMigrationProcessing.workflowType"
                    class="text-muted"
                  >
                    Permit (default)
                  </span>
                </dd>
              </dl>
            </div>
            <div
              class="col-md-6"
              *ngIf="
                selectedMigrationProcessing.workflowType === 'Renewable' &&
                selectedMigrationProcessing.expirationFormula
              "
            >
              <dl class="row">
                <dt class="col-sm-4">Expiration Formula:</dt>
                <dd class="col-sm-8">
                  <code class="text-success">{{
                    selectedMigrationProcessing.expirationFormula
                  }}</code>
                  <small class="d-block text-muted mt-1">
                    <i class="fa fa-info-circle me-1"></i>
                    Excel-style formula for calculating expiration date
                  </small>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <ul class="nav nav-tabs mb-3" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            [class.active]="activeTab === 'execution'"
            (click)="activeTab = 'execution'"
            type="button"
          >
            <i class="fa fa-play-circle me-2"></i>
            Execution Team
            <small
              class="d-block text-muted"
              style="font-size: 0.75rem; font-weight: normal;"
              >Run migration steps</small
            >
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            [class.active]="activeTab === 'data'"
            (click)="activeTab = 'data'"
            type="button"
          >
            <i class="fa fa-database me-2"></i>
            Data Team 2
            <small
              class="d-block text-muted"
              style="font-size: 0.75rem; font-weight: normal;"
              >File uploads & validation</small
            >
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            [class.active]="activeTab === 'history'"
            (click)="activeTab = 'history'; loadMigrationExecutions()"
            type="button"
          >
            <i class="fa fa-history me-2"></i>
            Migration History
            <small
              class="d-block text-muted"
              style="font-size: 0.75rem; font-weight: normal;"
              >Execution audit trail</small
            >
          </button>
        </li>
      </ul>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Execution Team Tab -->
        <div *ngIf="activeTab === 'execution'">
          <!-- Migration Data Entities Management -->
          <div class="mb-4" *ngIf="selectedMigrationProcessing">
            <!-- Show button if workflow is migrated and grid is not expanded -->
            <div
              *ngIf="
                selectedMigrationProcessing.workflowMigratedOn &&
                !showDataEntitiesGrid
              "
              class="card"
            >
              <div class="card-body text-center py-4">
                <p class="mb-3">
                  <i class="fa fa-table fa-2x text-muted"></i>
                </p>
                <p class="text-muted mb-3">
                  Migration data entities are configured and the workflow has
                  been migrated.
                </p>
                <button
                  class="btn btn-primary"
                  (click)="showDataEntitiesGrid = true"
                >
                  <i class="fa fa-edit me-2"></i>
                  Edit Data Entities
                </button>
              </div>
            </div>

            <!-- Show grid if workflow not migrated OR grid is expanded -->
            <div
              *ngIf="
                !selectedMigrationProcessing.workflowMigratedOn ||
                showDataEntitiesGrid
              "
            >
              <app-migration-processing-data-entities
                [migrationProcessingId]="selectedMigrationProcessing.id"
                (entitiesChanged)="onDataEntitiesChanged()"
              ></app-migration-processing-data-entities>
            </div>
          </div>

          <!-- Perform Migration Card -->
          <div class="card mb-4" *ngIf="canPerformMigration()">
            <div class="card-header bg-success text-white">
              <h6 class="mb-0">
                <i class="fa fa-upload me-2"></i>
                Step 1: Perform Workflow Migration
              </h6>
            </div>
            <div class="card-body">
              <!-- Migration Status -->
              <div
                *ngIf="selectedMigrationProcessing.workflowMigratedOn"
                class="alert alert-info mb-3"
              >
                <i class="fa fa-check-circle me-2"></i>
                <strong>Workflow Previously Migrated</strong>
                <div class="mt-2">
                  <small>
                    <strong>Migrated By:</strong>
                    {{ selectedMigrationProcessing.workflowMigratedBy }}<br />
                    <strong>Migrated On:</strong>
                    {{
                      selectedMigrationProcessing.workflowMigratedOn
                        | date: 'short'
                    }}
                  </small>
                </div>
              </div>
              <!-- First Time Migration -->
              <div *ngIf="!selectedMigrationProcessing.workflowMigratedOn">
                <p class="mb-3">
                  After reviewing and updating the migration data entities,
                  execute the migration.
                </p>
                <button
                  class="btn btn-success btn-lg"
                  (click)="startMigration()"
                  [disabled]="loading"
                >
                  <i class="fa fa-play me-2"></i>
                  Migrate Workflow
                </button>
              </div>

              <!-- Re-Migration -->
              <div *ngIf="selectedMigrationProcessing.workflowMigratedOn">
                <p class="mb-3">
                  You can update the migration data entities and migrate the
                  workflow again. This will update the existing workflow.
                </p>
                <button
                  class="btn btn-warning btn-lg"
                  (click)="startMigration()"
                  [disabled]="loading"
                >
                  <i class="fa fa-refresh me-2"></i>
                  Re-Process Data Entities and Migrate Again
                </button>
              </div>
            </div>
          </div>

          <!-- Migrate Application Data Card -->
          <div
            class="card mb-4"
            *ngIf="
              selectedMigrationProcessing.workflowMigratedOn &&
              selectedMigrationProcessing.applicationDataFilePath
            "
          >
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fa fa-database me-2"></i>
                Step 2: Migrate Application Data
              </h6>
            </div>
            <div class="card-body">
              <p class="mb-3">
                Execute the migration of application data into the workflow.
                This will create workflow applications from the uploaded JSON
                data.
              </p>

              <!-- First Time Migration -->
              <div
                *ngIf="!selectedMigrationProcessing.applicationDataMigratedOn"
              >
                <p class="mb-3">
                  <strong>{{
                    applicationDataValidationStatus?.recordCount || 0
                  }}</strong>
                  application record(s) ready to migrate.
                </p>

                <div
                  *ngIf="applicationDataMigrationError"
                  class="alert alert-danger alert-dismissible fade show mb-3"
                  role="alert"
                >
                  <i class="fa fa-exclamation-circle me-2"></i>
                  <strong>Migration Failed!</strong>
                  <p class="mb-0 mt-1">{{ applicationDataMigrationError }}</p>
                  <button
                    type="button"
                    class="btn-close"
                    (click)="applicationDataMigrationError = null"
                  ></button>
                </div>

                <button
                  class="btn btn-success btn-lg"
                  (click)="migrateApplicationData()"
                  [disabled]="
                    loading || !applicationDataValidationStatus?.isValid
                  "
                >
                  <i class="fa fa-play me-2"></i>
                  Execute Application Data Migration
                </button>
                <p
                  class="text-muted small mt-2 mb-0"
                  *ngIf="!applicationDataValidationStatus?.isValid"
                >
                  <i class="fa fa-info-circle me-1"></i>
                  Application data must be validated before migration. Go to the
                  Management tab to validate.
                </p>
              </div>

              <!-- Migration Complete -->
              <div
                *ngIf="selectedMigrationProcessing.applicationDataMigratedOn"
              >
                <div class="alert alert-success">
                  <i class="fa fa-check-circle me-2"></i>
                  <strong>Application data migrated successfully!</strong>
                  <p class="mb-0 mt-2">
                    Migrated by:
                    {{ selectedMigrationProcessing.applicationDataMigratedBy
                    }}<br />
                    Migrated on:
                    {{
                      selectedMigrationProcessing.applicationDataMigratedOn
                        | date: 'short'
                    }}
                  </p>
                </div>

                <div
                  *ngIf="applicationDataMigrationError"
                  class="alert alert-danger alert-dismissible fade show mb-3"
                  role="alert"
                >
                  <i class="fa fa-exclamation-circle me-2"></i>
                  <strong>Re-migration Failed!</strong>
                  <p class="mb-0 mt-1">{{ applicationDataMigrationError }}</p>
                  <button
                    type="button"
                    class="btn-close"
                    (click)="applicationDataMigrationError = null"
                  ></button>
                </div>

                <button
                  class="btn btn-warning btn-lg"
                  (click)="migrateApplicationData()"
                  [disabled]="loading"
                >
                  <i class="fa fa-refresh me-2"></i>
                  Re-Execute Application Data Migration
                </button>
                <p class="text-muted small mt-2 mb-0">
                  <i class="fa fa-exclamation-triangle me-1"></i>
                  Re-executing will update or create applications based on the
                  current application data file.
                </p>
              </div>
            </div>
          </div>

          <!-- Mark Complete Card -->
          <div class="card mb-4" *ngIf="canPerformMigration()">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fa fa-check-circle me-2"></i>
                Step 3: Mark as Complete
              </h6>
            </div>
            <div class="card-body">
              <p class="mb-3">
                Once the workflow migration has been successfully executed and
                verified, mark this workflow migration as complete.
              </p>

              <!-- Warning if application data is missing or not migrated -->
              <div
                class="alert alert-warning mb-3"
                *ngIf="!selectedMigrationProcessing.applicationDataFilePath"
              >
                <i class="fa fa-exclamation-triangle me-2"></i>
                <strong>Application data is required.</strong> Please ensure
                application data has been uploaded in the Management tab before
                marking this migration as complete.
              </div>
              <div
                class="alert alert-warning mb-3"
                *ngIf="
                  selectedMigrationProcessing.applicationDataFilePath &&
                  !selectedMigrationProcessing.applicationDataMigratedOn
                "
              >
                <i class="fa fa-exclamation-triangle me-2"></i>
                <strong>Application data migration is required.</strong> Please
                complete Step 3 to migrate the application data before marking
                this migration as complete.
              </div>

              <div class="d-flex gap-2">
                <button
                  class="btn btn-primary"
                  (click)="markAsComplete()"
                  [disabled]="
                    loading ||
                    !selectedMigrationProcessing.applicationDataFilePath ||
                    !selectedMigrationProcessing.applicationDataMigratedOn
                  "
                >
                  <i class="fa fa-check-circle me-2"></i>
                  Mark as Complete
                </button>
                <button
                  class="btn btn-danger"
                  (click)="deleteMigrationProcessing()"
                  [disabled]="loading"
                  title="Delete this workflow migration record"
                >
                  <i class="fa fa-trash me-2"></i>
                  Delete
                </button>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fa fa-info-circle me-1"></i>
                Marking as complete will remove the workflow migration from the
                ready-to-start list. Deleting will hide the record.
              </p>
            </div>
          </div>
        </div>
        <!-- End Execution Team Tab -->

        <!-- Data Team Tab -->
        <div *ngIf="activeTab === 'data'">
          <!-- File Re-Upload Card -->
          <div class="card mb-4" *ngIf="canPerformMigration()">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fa fa-upload me-2"></i>
                Re-Upload Migration Files
              </h6>
            </div>
            <div class="card-body">
              <p class="mb-3">
                You can update the metadata JSON file if needed before
                performing the migration.
              </p>

              <!-- Metadata JSON File Re-Upload -->
              <div class="form-group">
                <label class="form-label">
                  <strong>Metadata JSON File</strong>
                </label>
                <wm-upload
                  #metadataUploadComponent
                  buttonText="Re-Upload Metadata File"
                  validDocumentTypes="json"
                  [pathSegments]="[
                    'migration-processing',
                    selectedMigrationProcessing?.id,
                    'metadata'
                  ]"
                  (fileUploaded)="onMetadataFileUploaded($event)"
                  (fileDeleted)="onMetadataFileDeleted($event)"
                  [key]="'metadata-' + selectedMigrationProcessing?.id"
                  [allowMultipleFiles]="false"
                  category="MigrationProcessing"
                  [deleteStoredDocument]="false"
                  [showUploadedFilesList]="true"
                ></wm-upload>
                <small class="form-text text-muted">
                  Upload a new metadata JSON file to replace the current
                  metadata configuration.
                </small>
                <div
                  *ngIf="reprocessingMetadata"
                  class="alert alert-info mt-3 mb-0"
                  role="status"
                  aria-live="polite"
                >
                  <i class="fa fa-spinner fa-spin me-2" aria-hidden="true"></i>
                  Re-processing entity data from the metadata file...
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Section -->
          <div class="alert alert-warning" *ngIf="!canPerformMigration()">
            <i class="fa fa-lock me-2"></i>
            You are not the owner of this workflow migration. Only the owner can
            perform migrations and mark as complete.
          </div>

          <!-- Change Owner Dialog -->
          <div
            class="modal fade show d-block"
            *ngIf="showChangeOwnerDialog"
            tabindex="-1"
            style="background-color: rgba(0,0,0,0.5);"
          >
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">
                    <i class="fa fa-user-edit me-2"></i>
                    Change Owner
                  </h5>
                  <button
                    type="button"
                    class="btn-close"
                    (click)="cancelChangeOwner()"
                  ></button>
                </div>
                <div class="modal-body">
                  <p class="mb-3">
                    Select a new owner for this workflow migration:
                  </p>
                  <label for="newOwnerSelect" class="form-label">
                    <strong>New Owner</strong>
                    <span class="text-danger">*</span>
                  </label>
                  <select
                    id="newOwnerSelect"
                    class="form-control"
                    [(ngModel)]="newOwnerId"
                  >
                    <option [ngValue]="null">-- Select User --</option>
                    <option
                      *ngFor="let user of availableUsers"
                      [ngValue]="user.id"
                    >
                      {{ user.name }}
                    </option>
                  </select>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    (click)="cancelChangeOwner()"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    (click)="confirmChangeOwner()"
                    [disabled]="!newOwnerId || loading"
                  >
                    <i class="fa fa-check me-1"></i>
                    Change Owner
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Application Data Upload Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fa fa-database me-2"></i>
                Upload Application Data
                <span class="text-danger">*</span>
              </h6>
            </div>
            <div class="card-body">
              <p class="mb-3">
                Upload a JSON file containing application data for the migrated
                workflow. The data will be validated against the workflow's data
                entities.
                <strong class="text-danger"
                  >This is required before the migration can be marked as
                  complete.</strong
                >
              </p>

              <div class="form-group">
                <label class="form-label">
                  <strong>Application Data JSON File</strong>
                  <span class="text-danger">*</span>
                </label>
                <wm-upload
                  #applicationDataUploadComponent
                  buttonText="Select JSON File"
                  validDocumentTypes="json"
                  [pathSegments]="[
                    'migration-processing',
                    selectedMigrationProcessing?.id,
                    'application-data'
                  ]"
                  (fileUploaded)="onApplicationDataUploaded($event)"
                  (fileDeleted)="onApplicationDataDeleted($event)"
                  [key]="'app-data-' + selectedMigrationProcessing?.id"
                  [allowMultipleFiles]="false"
                  category="MigrationApplicationData"
                  [deleteStoredDocument]="false"
                  [showUploadedFilesList]="true"
                ></wm-upload>
                <small class="form-text text-muted">
                  Upload a JSON file with an array of application objects. Each
                  object should have properties matching the workflow's data
                  entity template codes.
                </small>
                <div class="mt-2">
                  <a
                    href="assets/sample-application-data-comprehensive.json"
                    download="sample-application-data-comprehensive.json"
                    class="btn btn-sm btn-outline-info"
                    target="_blank"
                  >
                    <i class="fa fa-download me-2"></i>
                    Download Sample Application Data File
                  </a>
                  <small class="form-text text-muted ml-2 d-inline">
                    Example application data with multiple applications
                  </small>
                </div>
              </div>

              <!-- Validate Button (shown if application data already uploaded) -->
              <div
                *ngIf="
                  selectedMigrationProcessing.applicationDataFilePath &&
                  !applicationDataValidationStatus
                "
                class="mt-3"
              >
                <button
                  class="btn btn-primary"
                  (click)="
                    validateApplicationData(
                      selectedMigrationProcessing.applicationDataFilePath
                    )
                  "
                  [disabled]="loading"
                >
                  <i class="fa fa-check-circle me-2"></i>
                  Validate Existing Application Data
                </button>
                <p class="text-muted small mt-2 mb-0">
                  <i class="fa fa-info-circle me-1"></i>
                  Application data file already uploaded. Click to validate
                  against the workflow.
                </p>
              </div>

              <!-- Validation Status -->
              <div *ngIf="applicationDataValidationStatus" class="mt-3">
                <div
                  class="alert"
                  [ngClass]="{
                    'alert-success': applicationDataValidationStatus.isValid,
                    'alert-danger':
                      !applicationDataValidationStatus.isValid &&
                      applicationDataValidationStatus.errors.length > 0,
                    'alert-info': applicationDataValidationStatus.isValidating
                  }"
                >
                  <div *ngIf="applicationDataValidationStatus.isValidating">
                    <i class="fa fa-spinner fa-spin me-2"></i>
                    <strong>Validating...</strong>
                  </div>
                  <div
                    *ngIf="
                      !applicationDataValidationStatus.isValidating &&
                      applicationDataValidationStatus.isValid
                    "
                  >
                    <i class="fa fa-check-circle me-2"></i>
                    <strong>Validation Passed!</strong>
                    <p class="mb-0 mt-2">
                      {{ applicationDataValidationStatus.recordCount }}
                      record(s) found with valid properties matching the
                      workflow data entities.
                    </p>
                  </div>
                  <div
                    *ngIf="
                      !applicationDataValidationStatus.isValidating &&
                      !applicationDataValidationStatus.isValid
                    "
                  >
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    <strong>Validation Failed</strong>
                    <ul class="mb-0 mt-2">
                      <li
                        *ngFor="
                          let error of applicationDataValidationStatus.errors
                        "
                      >
                        {{ error }}
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Validation Details -->
                <div
                  *ngIf="!applicationDataValidationStatus.isValidating"
                  class="mt-2"
                >
                  <button
                    class="btn btn-sm btn-outline-info"
                    type="button"
                    (click)="showValidationDetails = !showValidationDetails"
                  >
                    <i
                      class="fa"
                      [ngClass]="
                        showValidationDetails
                          ? 'fa-chevron-up'
                          : 'fa-chevron-down'
                      "
                    ></i>
                    {{ showValidationDetails ? 'Hide' : 'Show' }} Validation
                    Details
                  </button>

                  <div *ngIf="showValidationDetails" class="mt-3">
                    <h6>
                      Matched Properties ({{
                        applicationDataValidationStatus.matchedProperties
                          .length
                      }})
                    </h6>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <span
                        *ngFor="
                          let prop of applicationDataValidationStatus.matchedProperties
                        "
                        class="badge bg-success"
                      >
                        {{ prop }}
                      </span>
                    </div>
                    <p
                      *ngIf="
                        applicationDataValidationStatus.matchedProperties
                          .length === 0
                      "
                      class="text-muted small"
                    >
                      No properties in the application data match workflow data
                      entities.
                    </p>

                    <h6>
                      Unmatched Properties ({{
                        applicationDataValidationStatus.unmatchedProperties
                          .length
                      }})
                    </h6>
                    <p class="text-muted small mb-2">
                      Properties found in the application data that don't match
                      any workflow data entity.
                    </p>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <span
                        *ngFor="
                          let prop of applicationDataValidationStatus.unmatchedProperties
                        "
                        class="badge bg-warning text-dark"
                      >
                        {{ prop }}
                      </span>
                    </div>
                    <p
                      *ngIf="
                        applicationDataValidationStatus.unmatchedProperties
                          .length === 0
                      "
                      class="text-muted small"
                    >
                      All properties in the application data match workflow data
                      entities.
                    </p>

                    <h6>
                      Missing Required Properties ({{
                        applicationDataValidationStatus
                          .missingRequiredProperties.length
                      }})
                    </h6>
                    <p class="text-muted small mb-2">
                      Required workflow data entities not found in the
                      application data.
                    </p>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <span
                        *ngFor="
                          let prop of applicationDataValidationStatus.missingRequiredProperties
                        "
                        class="badge bg-danger"
                      >
                        {{ prop }}
                      </span>
                    </div>
                    <p
                      *ngIf="
                        applicationDataValidationStatus
                          .missingRequiredProperties.length === 0
                      "
                      class="text-muted small"
                    >
                      All required properties are present in the application
                      data.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Help Documentation Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fa fa-question-circle me-2"></i>
                File Format Documentation
              </h6>
            </div>
            <div class="card-body">
              <!-- JSON Metadata File Format -->
              <div class="mb-4">
                <button
                  class="btn btn-outline-info btn-sm mb-2"
                  type="button"
                  (click)="showMetadataHelp = !showMetadataHelp"
                >
                  <i
                    class="fa"
                    [ngClass]="
                      showMetadataHelp ? 'fa-chevron-up' : 'fa-chevron-down'
                    "
                  ></i>
                  {{ showMetadataHelp ? 'Hide' : 'Show' }} JSON Metadata File
                  Format
                </button>

                <div
                  *ngIf="showMetadataHelp"
                  class="border-start border-3 border-info ps-3"
                >
                  <h6 class="mt-3">JSON Metadata File Structure</h6>
                  <p>
                    The JSON metadata file describes the workflow structure and
                    field configurations:
                  </p>

                  <h6 class="mt-3">Basic Structure:</h6>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "Activities": [
    {{"{"}}
      "ActivityName": "Applicant Information",
      "Type": "form",
      "Fields": [...]
    {{"}"}}
  ],
  "ReferencesParcels": false,
  "Transactions": []
{{"}"}}</code></pre>

                  <h6 class="mt-3">Supported Activity Types:</h6>
                  <ul>
                    <li>
                      <code class="text-primary">form</code> - Standard data
                      entry form (default)
                    </li>
                    <li>
                      <code class="text-primary">payment</code> - Payment
                      collection activity
                    </li>
                    <li>
                      <code class="text-primary">printtemplate</code> or
                      <code class="text-primary">print</code> - Document
                      generation activity
                    </li>
                    <li>
                      <code class="text-primary">signoff</code> - Review and
                      approval activity
                    </li>
                    <li>
                      <code class="text-primary">email</code> - Send email
                      notifications
                    </li>
                    <li>
                      <code class="text-primary">inspection</code> or
                      <code class="text-primary">consolidated-inspection</code>
                      - Consolidated inspection activity
                    </li>
                    <li>
                      <code class="text-primary">end</code> or
                      <code class="text-primary">complete</code> - Complete/end
                      workflow
                    </li>
                  </ul>

                  <h6 class="mt-3">Supported Field Types:</h6>
                  <div class="row">
                    <div class="col-md-6">
                      <ul class="small">
                        <li>
                          <code class="text-success">text</code> - Single line
                          text input
                        </li>
                        <li>
                          <code class="text-success">textarea</code> -
                          Multi-line text input
                        </li>
                        <li>
                          <code class="text-success">number</code> - Numeric
                          input
                        </li>
                        <li>
                          <code class="text-success">decimal</code> - Decimal
                          number
                        </li>
                        <li>
                          <code class="text-success">currency</code> - Money
                          amount
                        </li>
                        <li>
                          <code class="text-success">date</code> - Date picker
                        </li>
                        <li>
                          <code class="text-success">datetime</code> - Date and
                          time picker
                        </li>
                        <li>
                          <code class="text-success">time</code> - Time picker
                        </li>
                        <li>
                          <code class="text-success">today</code> -
                          Auto-populates today's date
                        </li>
                        <li>
                          <code class="text-success">checkbox</code> - Checkbox
                          selection
                        </li>
                        <li>
                          <code class="text-success">dropdown</code> - Dropdown
                          list
                        </li>
                        <li>
                          <code class="text-success">calculated</code> -
                          Calculated/formula field
                        </li>
                        <li>
                          <code class="text-success">fee</code> - Fee
                          calculation field
                        </li>
                      </ul>
                    </div>
                    <div class="col-md-6">
                      <ul class="small">
                        <li>
                          <code class="text-success">radio</code> - Radio button
                          group
                        </li>
                        <li>
                          <code class="text-success">email</code> - Email
                          address input
                        </li>
                        <li>
                          <code class="text-success">phone</code> - Phone number
                          input
                        </li>
                        <li>
                          <code class="text-success">file</code> - File upload
                        </li>
                        <li>
                          <code class="text-success">image</code> - Image upload
                        </li>
                        <li>
                          <code class="text-success">signature</code> -
                          Signature capture field
                        </li>
                        <li>
                          <code class="text-success">formattedtext</code> - Rich
                          text/HTML content
                        </li>
                        <li>
                          <code class="text-success">group</code> - Grouped
                          fields container
                        </li>
                        <li>
                          <code class="text-success">row</code> - Horizontal
                          field layout
                        </li>
                        <li>
                          <code class="text-success">table</code> - Table grid
                          layout
                        </li>
                      </ul>
                    </div>
                  </div>

                  <h6 class="mt-3">Example Field Configuration:</h6>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "Label": "First Name",
  "Type": "text",
  "Name": "FIRST_NAME",
  "Required": true,
  "MatchedTemplateCode": "APPLICANT_FIRST_NAME"
{{"}"}}</code></pre>

                  <h6 class="mt-3">Field with Options (Dropdown/Radio):</h6>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "Label": "Application Type",
  "Type": "dropdown",
  "Name": "APP_TYPE",
  "Required": true,
  "Options": [
    {{"{"}} "Label": "New", "Value": "NEW" {{"}"}},
    {{"{"}} "Label": "Renewal", "Value": "RENEWAL" {{"}"}}
  ]
{{"}"}}</code></pre>

                  <h6 class="mt-3">Group Layout (Grouped Fields):</h6>
                  <p class="small">
                    Groups multiple fields together visually in a single
                    container:
                  </p>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "Label": "Contact Information",
  "Type": "group",
  "Name": "CONTACT_GROUP",
  "ChildFields": [
    {{"{"}}
      "Label": "Phone Number",
      "Type": "phone",
      "Name": "PHONE",
      "Required": true,
      "MatchedTemplateCode": "CONTACT_PHONE",
    {{"}"}},
    {{"{"}}
      "Label": "Email Address",
      "Type": "email",
      "Name": "EMAIL",
      "Required": true,
      "MatchedTemplateCode": "CONTACT_EMAIL",
    {{"}"}},
    {{"{"}}
      "Label": "Preferred Contact Method",
      "Type": "dropdown",
      "Name": "CONTACT_METHOD",
      "Required": false,
      "MatchedTemplateCode": "CONTACT_PREF",
      "Options": [
        {{"{'}} "Label": "Email", "Value": "EMAIL" {{'}'}},
        {{"{'}} "Label": "Phone", "Value": "PHONE" {{'}'}}
      ]
    {{"}"}}
  ]
{{"}"}}</code></pre>

                  <h6 class="mt-3">Row Layout (Horizontal Fields):</h6>
                  <p class="small">
                    Arranges multiple fields horizontally in a single row:
                  </p>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "Type": "row",
  "Name": "NAME_ROW",
  "ChildFields": [
    {{"{"}}
      "Label": "First Name",
      "Type": "text",
      "Name": "FIRST_NAME",
      "Required": true,
      "MinLength": 2,
      "MatchedTemplateCode": "APPLICANT_FIRST_NAME",
    {{"}"}},
    {{"{"}}
      "Label": "Middle Initial",
      "Type": "text",
      "Name": "MIDDLE_INITIAL",
      "Required": false,
      "MinLength": 1,
      "MatchedTemplateCode": "APPLICANT_MIDDLE_INITIAL",
    {{"}"}},
    {{"{"}}
      "Label": "Last Name",
      "Type": "text",
      "Name": "LAST_NAME",
      "Required": true,
      "MinLength": 2,
      "MatchedTemplateCode": "APPLICANT_LAST_NAME",
    {{"}"}}
  ]
{{"}"}}</code></pre>

                  <h6 class="mt-3">Table Layout (Structured Grid):</h6>
                  <p class="small">
                    Creates a table with static text and fields in a grid
                    structure. Each cell can contain either StaticText or an
                    Entity (field):
                  </p>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "Type": "table",
  "Name": "ADDRESS_TABLE",
  "TableLayout": {{"{"}}
    "Rows": [
      {{"{"}}
        "Cells": [
          {{"{"}} "StaticText": "Street Address:" {{"}"}},
          {{"{"}} "Entity": {{"{"}}
            "Type": "text",
            "Name": "STREET",
            "Label": "",
            "Required": true,
            "MatchedTemplateCode": "PROPERTY_STREET"
          {{"}"}} {{"}"}}
        ]
      {{"}"}},
      {{"{"}}
        "Cells": [
          {{"{"}} "StaticText": "City:" {{"}"}},
          {{"{"}} "Entity": {{"{"}}
            "Type": "text",
            "Name": "CITY",
            "Label": "",
            "Required": true,
            "MatchedTemplateCode": "PROPERTY_CITY",
          {{"}"}} {{"}"}},
          {{"{"}} "StaticText": "State:" {{"}"}},
          {{"{"}} "Entity": {{"{"}}
            "Type": "dropdown",
            "Name": "STATE",
            "Label": "",
            "Required": true,
            "MatchedTemplateCode": "PROPERTY_STATE",
            "Options": [
              {{"{"}} "Label": "CA", "Value": "CA" {{"}"}},
              {{"{"}} "Label": "NY", "Value": "NY" {{"}"}}
            ]
          {{"}"}} {{"}"}}
        ]
      {{"}"}},
      {{"{"}}
        "Cells": [
          {{"{"}} "StaticText": "ZIP Code:" {{"}"}},
          {{"{"}} "Entity": {{"{"}}
            "Type": "text",
            "Name": "ZIP",
            "Label": "",
            "Required": true,
            "MinLength": 5,
            "MatchedTemplateCode": "PROPERTY_ZIP",
          {{"}"}} {{"}"}}
        ]
      {{"}"}}
    ]
  {{"}"}}
{{"}"}}</code></pre>

                  <div class="alert alert-info small mt-3">
                    <i class="fa fa-info-circle me-2"></i>
                    <strong>Tip:</strong> Use the metadata validation in the
                    creation step to verify your field types are supported
                    before uploading.
                  </div>
                </div>
              </div>

              <!-- Application Data JSON Format -->
              <div class="mt-4">
                <button
                  class="btn btn-outline-info btn-sm mb-2"
                  type="button"
                  (click)="showApplicationDataHelp = !showApplicationDataHelp"
                >
                  <i
                    class="fa"
                    [ngClass]="
                      showApplicationDataHelp
                        ? 'fa-chevron-up'
                        : 'fa-chevron-down'
                    "
                  ></i>
                  {{ showApplicationDataHelp ? 'Hide' : 'Show' }} Application
                  Data JSON Format
                </button>

                <div
                  *ngIf="showApplicationDataHelp"
                  class="border-start border-3 border-info ps-3"
                >
                  <h6 class="mt-3">Application Data JSON Structure</h6>
                  <p>
                    The application data file contains actual data to migrate as
                    workflow applications. It uses a nested structure grouped by
                    unique identifier:
                  </p>

                  <h6 class="mt-3">File Structure:</h6>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "AutoGenId_1": {{"{"}}
    "APPLICANT INFORMATION": [
      {{"{"}}
        "FIRST_NAME": "John",
        "LAST_NAME": "Smith",
        "EMAIL": "john.smith@example.com",
        "APPLICATION_DATE": "2024-01-15"
      {{"}"}}
    ],
    "PROPERTY INFORMATION": [
      {{"{"}}
        "PROPERTY_ADDRESS": "123 Main St",
        "CITY": "Springfield"
      {{"}"}}
    ]
  {{"}"}},
  "AutoGenId_2": {{"{"}}
    "APPLICANT INFORMATION": [
      {{"{"}}
        "FIRST_NAME": "Jane",
        "LAST_NAME": "Doe",
        "EMAIL": "jane.doe@example.com"
      {{"}"}}
    ]
  {{"}"}}
{{"}"}}</code></pre>

                  <h6 class="mt-3">Structure Explanation:</h6>
                  <ul>
                    <li>
                      <strong>Top Level Keys (AutoGenId):</strong> Unique
                      identifier for each application. All data under this key
                      will be merged into a single workflow application.
                    </li>
                    <li>
                      <strong>Second Level Keys (Activity Name):</strong>
                      Activity names from the source system (e.g., "APPLICANT
                      INFORMATION", "PROPERTY INFORMATION"). Used to organize
                      data by activity/section. Data from all activities for the
                      same AutoGenId will be combined.
                    </li>
                    <li>
                      <strong>Third Level (array of objects):</strong> Array
                      containing data records. Typically one record per
                      AutoGenId per activity, but multiple records will be
                      merged.
                    </li>
                    <li>
                      <strong>Data Properties:</strong> Keys should match the
                      Template Codes from your workflow's data entities. Values
                      will be imported into the workflow application.
                    </li>
                  </ul>

                  <h6 class="mt-3">Property Matching:</h6>
                  <p class="small">
                    Property names in your JSON must match the
                    <strong>Template Codes</strong> defined in your workflow's
                    data entities. For example:
                  </p>
                  <ul class="small">
                    <li>
                      If your workflow has a data entity with Template Code
                      <code class="text-primary">APPLICANT_FIRST_NAME</code>,
                      your JSON should use
                      <code class="text-primary"
                        >"APPLICANT_FIRST_NAME": "John"</code
                      >
                    </li>
                    <li>
                      Properties that don't match any data entity will be
                      flagged during validation
                    </li>
                    <li>
                      Required data entities that are missing will cause
                      validation errors
                    </li>
                  </ul>

                  <h6 class="mt-3">Data Merging:</h6>
                  <p class="small">When migrating application data:</p>
                  <ul class="small">
                    <li>
                      All records under the same AutoGenId are merged into one
                      application
                    </li>
                    <li>Properties from different activities are combined</li>
                    <li>
                      If the same property appears in multiple places, the first
                      occurrence is used
                    </li>
                    <li>Each AutoGenId creates one workflow application</li>
                  </ul>

                  <h6 class="mt-3">Example for Dog License Application:</h6>
                  <pre class="bg-light p-2 rounded"><code>{{"{"}}
  "157168": {{"{"}}
    "DOG OWNER INFORMATION": [
      {{"{"}}
        "AppName": "John Smith",
        "AppEmail": "john@example.com",
        "AppPhone": "555-0123",
        "AppStNum": "123",
        "AppStName": "Main St"
      {{"}"}}
    ],
    "DOG DETAILS": [
      {{"{"}}
        "DogName": "Max",
        "Breed": "Labrador",
        "Gender": "Male",
        "PriColor": "Yellow",
        "VaccDate": "2024-01-10"
      {{"}"}}
    ]
  {{"}"}}
{{"}"}}</code></pre>

                  <div class="alert alert-warning small mt-3">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    <strong>Important:</strong>
                    <ul class="mb-0">
                      <li>
                        Always validate your application data before migration
                      </li>
                      <li>
                        Ensure property names match exactly (case-sensitive)
                      </li>
                      <li>
                        The validation report will show matched, unmatched, and
                        missing properties
                      </li>
                      <li>
                        Migration will create one application per unique
                        AutoGenId
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- End Data Team Tab -->

        <!-- Migration History Tab -->
        <div *ngIf="activeTab === 'history'">
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between"
            >
              <h6 class="mb-0">
                <i class="fa fa-history me-2"></i>
                Migration Execution History
              </h6>
              <button
                class="btn btn-sm btn-outline-secondary"
                (click)="loadMigrationExecutions()"
                [disabled]="loadingHistory"
                title="Refresh"
              >
                <i class="fa fa-refresh" [class.fa-spin]="loadingHistory"></i>
              </button>
            </div>
            <div class="card-body p-0">
              <!-- Loading state -->
              <div *ngIf="loadingHistory" class="text-center py-4">
                <i class="fa fa-spinner fa-spin me-2"></i>
                Loading history&hellip;
              </div>

              <!-- Empty state -->
              <div
                *ngIf="
                  !loadingHistory &&
                  migrationExecutions.length === 0 &&
                  migrationApplicationHistories.length === 0
                "
                class="text-center text-muted py-4"
              >
                <i class="fa fa-history fa-2x mb-2 d-block"></i>
                No migration history recorded yet.
              </div>

              <!-- Execution records table -->
              <div
                *ngIf="!loadingHistory && migrationExecutions.length > 0"
                class="table-responsive"
              >
                <table class="table table-hover table-sm mb-0">
                  <colgroup>
                    <col style="width: 2.5rem;" />
                    <col style="width: 11rem;" />
                    <col />
                    <col style="width: 9rem;" />
                    <col style="width: 6rem;" />
                    <col />
                  </colgroup>
                  <thead class="table-light">
                    <tr>
                      <th>#</th>
                      <th>Executed On</th>
                      <th>Executed By</th>
                      <th>Result Workflow</th>
                      <th>JSON</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let exec of migrationExecutions; let i = index">
                      <td class="text-muted" style="width: 3rem;">
                        {{ migrationExecutions.length - i }}
                      </td>
                      <td style="white-space: nowrap;">
                        {{ exec.executedOn | date: 'MM/dd/yyyy h:mm a' }}
                      </td>
                      <td>
                        <span *ngIf="exec.executedBy; else noUser">{{
                          exec.executedBy
                        }}</span>
                        <ng-template #noUser>
                          <span class="text-muted">—</span>
                        </ng-template>
                      </td>
                      <td>
                        <a
                          *ngIf="exec.resultWorkflowId"
                          [routerLink]="
                            hasClientContext
                              ? ['/admin/workflow-builder', exec.resultWorkflowId]
                              : ['/workflow', exec.resultWorkflowId]
                          "
                          target="_blank"
                          class="btn btn-sm btn-outline-primary"
                          title="Open the workflow created in this run (ID: {{ exec.resultWorkflowId }})"
                        >
                          <i class="fa fa-external-link me-1"></i>Open Workflow
                        </a>
                        <span *ngIf="!exec.resultWorkflowId" class="text-muted"
                          >—</span
                        >
                      </td>
                      <td>
                        <button
                          *ngIf="exec.csvDataFilePath"
                          class="btn btn-sm btn-outline-secondary"
                          (click)="downloadExecutionMetadataFile(exec.csvDataFilePath)"
                          title="Download the JSON file used for this run"
                        >
                          <i class="fa fa-download me-1"></i>JSON
                        </button>
                        <span *ngIf="!exec.csvDataFilePath" class="text-muted">—</span>
                      </td>
                      <td>
                        <span
                          *ngIf="exec.executionErrors"
                          class="text-danger"
                          title="{{ exec.executionErrors }}"
                        >
                          <i class="fa fa-exclamation-triangle me-1"></i>
                          {{ exec.executionErrors | slice: 0:80
                          }}{{ exec.executionErrors.length > 80 ? '…' : '' }}
                        </span>
                        <span *ngIf="!exec.executionErrors" class="text-muted">
                          <i class="fa fa-check text-success me-1"></i>None
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div
              *ngIf="!loadingHistory && migrationExecutions.length > 0"
              class="card-footer text-muted small"
            >
              {{ migrationExecutions.length }} run{{
                migrationExecutions.length !== 1 ? 's' : ''
              }}
              recorded &mdash; newest first
            </div>

            <div class="card-header border-top">
              <h6 class="mb-0">
                <i class="fa fa-list-alt me-2"></i>
                Application Data Migration History
              </h6>
            </div>
            <div class="card-body p-0">
              <div
                *ngIf="!loadingHistory && migrationApplicationHistories.length === 0"
                class="text-center text-muted py-4"
              >
                No applications have been tracked for this migration yet.
              </div>

              <div
                *ngIf="!loadingHistory && migrationApplicationHistories.length > 0"
                class="table-responsive"
              >
                <table class="table table-hover table-sm mb-0">
                  <colgroup>
                    <col style="width: 2.5rem;" />
                    <col />
                    <col style="width: 10rem;" />
                    <col style="width: 11rem;" />
                    <col style="width: 10rem;" />
                    <col style="width: 8rem;" />
                  </colgroup>
                  <thead class="table-light">
                    <tr>
                      <th>#</th>
                      <th>Application Identifier</th>
                      <th>Application</th>
                      <th>Migrated On</th>
                      <th>Migrated By</th>
                      <th>File</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let history of migrationApplicationHistories; let i = index">
                      <td class="text-muted">{{ migrationApplicationHistories.length - i }}</td>
                      <td>{{ history.applicationIdentifier || '—' }}</td>
                      <td>
                        <button
                          *ngIf="history.workflowApplicationId"
                          class="btn btn-sm btn-outline-primary"
                          (click)="openMigratedApplication(history.workflowApplicationId)"
                          title="Open migrated application"
                        >
                          <i class="fa fa-external-link me-1"></i>Open
                        </button>
                        <span *ngIf="!history.workflowApplicationId" class="text-muted">—</span>
                      </td>
                      <td style="white-space: nowrap;">
                        {{ history.migratedOn | date: 'MM/dd/yyyy h:mm a' }}
                      </td>
                      <td>{{ history.migratedBy || '—' }}</td>
                      <td>
                        <button
                          *ngIf="history.applicationDataFilePath"
                          class="btn btn-sm btn-outline-secondary"
                          (click)="downloadExecutionMetadataFile(history.applicationDataFilePath)"
                          title="Download the application data file used for this migration record"
                        >
                          <i class="fa fa-download me-1"></i>File
                        </button>
                        <span *ngIf="!history.applicationDataFilePath" class="text-muted">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div
              *ngIf="!loadingHistory && migrationApplicationHistories.length > 0"
              class="card-footer text-muted small"
            >
              {{ migrationApplicationHistories.length }} application record{{
                migrationApplicationHistories.length !== 1 ? 's' : ''
              }}
              tracked &mdash; newest first
            </div>
          </div>
        </div>
        <!-- End Migration History Tab -->
      </div>
      <!-- End Tab Content -->
    </div>
  </div>

  <!-- Migration Form Section -->
  <div *ngIf="showMigrationForm">
    <!-- Header with back button -->
    <div class="row mb-3">
      <div class="col">
        <button
          class="btn btn-outline-secondary"
          (click)="showMigrationForm = false"
        >
          <i class="fa fa-arrow-left me-2"></i>
          Back to
          {{ selectedMigrationProcessing ? 'Migration Details' : 'List' }}
        </button>
        <h4 class="mt-3">
          <i class="fa fa-upload me-2"></i>
          {{
            selectedMigrationProcessing ? 'Updated Migration' : 'New Migration'
          }}
        </h4>
        <p class="text-muted" *ngIf="selectedMigrationProcessing">
          For workflow migration:
          <strong>{{ selectedMigrationProcessing.sourceSystem }}</strong>
        </p>
      </div>
    </div>

    <!-- Migration Data Entity Help -->
    <div class="row mb-4">
      <div class="col">
        <div class="alert alert-info">
          <h6 class="alert-heading">
            <i class="fa fa-info-circle me-2"></i>
            Migration Data Entity Configuration
          </h6>
          <p class="mb-2">
            The migration data entities are automatically created from your
            uploaded metadata file and contain the following information:
          </p>

          <p class="mb-1"><strong>Reference Fields (Source System):</strong></p>
          <ul class="mb-2">
            <li>
              <strong>Section</strong> - The section of the application form
              where the input was located in the source system
            </li>
            <li>
              <strong>Input Name</strong> - The name of the input field on the
              application form in the source system
            </li>
            <li>
              <strong>Input Type</strong> - The type of input that was found in
              the source system (e.g., "Text", "Number", "Date", "Checkbox",
              "Select")
            </li>
          </ul>

          <p class="mb-1">
            <strong>Editable Fields (Migration Configuration):</strong>
          </p>
          <ul class="mb-2">
            <li>
              <strong>Label</strong> - The label of the input field on the
              application form in the source system
            </li>
            <li>
              <strong>Template Code</strong> - The template code to use for the
              migrated workflow (defaults to Input Name from the source system)
            </li>
            <li>
              <strong>Target Activity</strong> - The name of the activity in the
              migrated workflow where this input should be located
            </li>
          </ul>
          <p class="mb-0">
            <small class="text-muted">
              <strong>Note:</strong> Review and update the data entities in the
              table above before executing the migration. You can edit fields
              inline, bulk update multiple entities, and reorder them as needed.
              <br />
              <strong>Important:</strong> Removing data entities will cause the
              source inputs to not be created in the migrated workflow or be
              removed from an existing workflow.
              <br />
              <strong>Important:</strong> If the workflow name you specify
              already exists, you must check the "Overwrite existing workflow"
              option in the summary section if you want to replace the existing
              workflow. This process cannot be undone. If you do not select to
              overwrite an existing workflow, the migration will update the
              existing workflow. Entities and activities will be added/removed
              to match the data entity configuration.
            </small>
          </p>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div
      *ngIf="success"
      class="alert alert-success alert-dismissible fade show"
      role="alert"
    >
      <i class="fa fa-check-circle me-2"></i>
      <strong>Success!</strong> Workflow migration completed successfully.
      <button
        type="button"
        class="btn-close"
        (click)="success = false"
      ></button>
    </div>

    <!-- Error Message -->
    <div
      *ngIf="error"
      class="alert alert-danger alert-dismissible fade show"
      role="alert"
    >
      <i class="fa fa-exclamation-circle me-2"></i>
      <strong>Error!</strong> {{ error }}
      <button type="button" class="btn-close" (click)="error = null"></button>
    </div>

    <!-- Loader -->
    <app-loader *ngIf="loading"></app-loader>

    <!-- Main Form -->
    <div *ngIf="!loading" class="card">
      <div class="card-header">
        <h5 class="mb-0">Migration Configuration</h5>
      </div>
      <div class="card-body">
        <!-- Template Workflow Selection -->
        <div class="row mb-3" *ngIf="hasClientContext">
          <div class="col-md-12">
            <label for="templateWorkflow" class="form-label">
              <strong>Template Workflow (Optional)</strong>
            </label>
            <select
              id="templateWorkflow"
              class="form-control"
              [(ngModel)]="selectedWorkflowId"
              (ngModelChange)="onWorkflowSelected($event)"
              [disabled]="loading || migrationSummary !== null"
            >
              <option [ngValue]="null">-- Create New Workflow --</option>
              <option
                *ngFor="let workflow of templateWorkflows"
                [ngValue]="workflow.id"
              >
                {{ workflow.version?.name || workflow.name }}
              </option>
            </select>
            <small class="form-text text-muted">
              Select a template workflow to use as a starting point, or create a
              new workflow from scratch.
            </small>
          </div>
        </div>

        <!-- Workflow Name -->
        <div class="row mb-3">
          <div class="col-md-12">
            <label for="workflowName" class="form-label">
              <strong>Workflow Name</strong>
              <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              id="workflowName"
              [(ngModel)]="workflowName"
              placeholder="Enter the name for the migrated workflow"
              [disabled]="loading || migrationSummary !== null"
              maxlength="255"
            />
            <small class="form-text text-muted">
              Specify the name for the workflow that will be created.
            </small>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="row">
          <div class="col-md-12">
            <button
              class="btn btn-primary me-2"
              (click)="generateSummary()"
              [disabled]="!canGenerateSummary() || migrationSummary !== null"
            >
              <i class="fa fa-search me-1"></i>
              Generate Summary
            </button>
            <button
              class="btn btn-secondary"
              (click)="reset()"
              [disabled]="loading"
            >
              <i class="fa fa-redo me-1"></i>
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Migration Summary -->
    <div *ngIf="migrationSummary && !loading" class="card mt-4">
      <div class="card-header bg-info text-white">
        <h5 class="mb-0">
          <i class="fa fa-info-circle me-2"></i>
          Migration Summary
        </h5>
      </div>
      <div class="card-body">
        <!-- Errors -->
        <div *ngIf="hasErrors()" class="alert alert-danger">
          <h6 class="alert-heading">
            <i class="fa fa-exclamation-triangle me-2"></i>
            Errors Found
          </h6>
          <ul class="mb-0">
            <li *ngFor="let err of migrationSummary.errorMessages">
              {{ err }}
            </li>
          </ul>
        </div>

        <!-- Hierarchical Activity and Data Entity View -->
        <div class="row">
          <div class="col-12 mb-3">
            <h6 class="mb-3">Activities and Data Entities</h6>

            <div *ngIf="hierarchicalActivities.length === 0" class="text-muted">
              No changes to display
            </div>

            <div *ngFor="let activity of hierarchicalActivities" class="mb-3">
              <div class="card">
                <div
                  class="card-header d-flex justify-content-between align-items-center"
                  [ngClass]="{
                    'bg-success text-white': activity.action === 'add',
                    'bg-danger text-white': activity.action === 'remove'
                  }"
                  style="cursor: pointer;"
                  (click)="toggleActivity(activity.name)"
                >
                  <div>
                    <i
                      class="fa me-2"
                      [ngClass]="{
                        'fa-plus-circle': activity.action === 'add',
                        'fa-minus-circle': activity.action === 'remove'
                      }"
                    ></i>
                    <strong>{{ activity.name }}</strong>
                    <span
                      *ngIf="activity.dataEntities.length > 0"
                      class="badge bg-light text-dark ms-2"
                    >
                      {{ activity.dataEntities.length }} data
                      {{
                        activity.dataEntities.length === 1
                          ? 'entity'
                          : 'entities'
                      }}
                    </span>
                  </div>
                  <i
                    class="fa"
                    [ngClass]="{
                      'fa-chevron-down': !isActivityExpanded(activity.name),
                      'fa-chevron-up': isActivityExpanded(activity.name)
                    }"
                  ></i>
                </div>

                <!-- Data Entities (Expandable) -->
                <div
                  *ngIf="
                    isActivityExpanded(activity.name) &&
                    activity.dataEntities.length > 0
                  "
                  class="card-body"
                >
                  <h6 class="mb-2">Data Entities:</h6>
                  <ul class="list-unstyled mb-0">
                    <li
                      *ngFor="let entity of activity.dataEntities"
                      class="mb-2 d-flex align-items-center"
                    >
                      <i
                        class="fa me-2"
                        [ngClass]="{
                          'fa-plus-circle text-success':
                            entity.action === 'add',
                          'fa-minus-circle text-danger':
                            entity.action === 'remove'
                        }"
                      ></i>
                      <strong>{{ entity.templateCode }}</strong>
                      <span
                        class="badge ms-2"
                        [ngClass]="{
                          'bg-success': entity.action === 'add',
                          'bg-danger': entity.action === 'remove'
                        }"
                      >
                        {{ entity.action === 'add' ? 'Add' : 'Remove' }}
                      </span>
                    </li>
                  </ul>
                </div>

                <!-- No Data Entities Message -->
                <div
                  *ngIf="
                    isActivityExpanded(activity.name) &&
                    activity.dataEntities.length === 0
                  "
                  class="card-body text-muted"
                >
                  No data entities for this activity
                </div>
              </div>
            </div>
          </div>

          <!-- Selected Template Info -->
          <div class="col-md-6 mb-3">
            <div class="card">
              <div class="card-header bg-secondary text-white">
                <h6 class="mb-0">
                  <i class="fa fa-info-circle me-2"></i>
                  Configuration
                </h6>
              </div>
              <div class="card-body">
                <p class="mb-2">
                  <strong>Workflow Name:</strong><br />
                  {{ workflowName }}
                </p>
                <p class="mb-2">
                  <strong>Template Workflow:</strong><br />
                  {{ getSelectedWorkflowName() }}
                </p>
                <p class="mb-2">
                  <strong>Overwrite if Exists:</strong><br />
                  {{ overwriteWorkflow ? 'Yes' : 'No' }}
                </p>
                <p class="mb-0">
                  <strong>CSV File:</strong><br />
                  {{ fileName }}
                </p>
              </div>
            </div>
          </div>

          <!-- Custom Lists to Import -->
          <div
            *ngIf="
              migrationSummary?.customLists &&
              migrationSummary.customLists.length > 0
            "
            class="col-12 mb-3"
          >
            <div class="card">
              <div class="card-header bg-primary text-white">
                <h6 class="mb-0">
                  <i class="fa fa-list me-2"></i>
                  Custom Lists to Import
                  <span class="badge bg-light text-dark ms-2">{{
                    migrationSummary.customLists.length
                  }}</span>
                </h6>
              </div>
              <div class="card-body">
                <p *ngIf="!hasExistingCustomLists()" class="mb-3 text-muted">
                  The following custom lists will be created during the
                  migration:
                </p>
                <div
                  *ngIf="hasExistingCustomLists()"
                  class="alert alert-warning mb-3"
                >
                  <h6 class="alert-heading">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    Custom List Conflicts Detected
                  </h6>
                  <p class="mb-2">
                    Some custom lists from the migration file already exist in
                    your system. By default, existing lists will be skipped to
                    avoid data loss.
                  </p>
                  <p class="mb-0">
                    <strong>Check the box</strong> next to any existing list if
                    you want to <strong>replace it</strong> with the version
                    from the migration file. This will delete all existing items
                    and attributes and replace them with the imported data.
                  </p>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th
                          style="width: 50px;"
                          *ngIf="hasExistingCustomLists()"
                        >
                          Update?
                        </th>
                        <th>List Name</th>
                        <th class="text-center">Items</th>
                        <th class="text-center">Attributes</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        *ngFor="let customList of migrationSummary.customLists"
                        [class.table-warning]="
                          customList.alreadyExists &&
                          !isCustomListSelectedForUpdate(customList.name)
                        "
                        [class.table-success]="
                          customList.alreadyExists &&
                          isCustomListSelectedForUpdate(customList.name)
                        "
                      >
                        <td
                          *ngIf="hasExistingCustomLists()"
                          class="text-center"
                        >
                          <input
                            *ngIf="customList.alreadyExists"
                            type="checkbox"
                            class="form-check-input"
                            [checked]="
                              isCustomListSelectedForUpdate(customList.name)
                            "
                            (change)="toggleCustomListUpdate(customList.name)"
                            title="Check to replace existing custom list"
                          />
                          <span
                            *ngIf="!customList.alreadyExists"
                            class="text-muted"
                            >-</span
                          >
                        </td>
                        <td>
                          <strong>{{ customList.name }}</strong>
                          <span
                            *ngIf="customList.alreadyExists"
                            class="badge bg-warning text-dark ms-2"
                          >
                            <i class="fa fa-exclamation-circle"></i> Exists
                          </span>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-info">{{
                            customList.itemCount
                          }}</span>
                        </td>
                        <td class="text-center">
                          <span
                            *ngIf="customList.attributeCount > 0"
                            class="badge bg-success"
                            >{{ customList.attributeCount }}</span
                          >
                          <span
                            *ngIf="customList.attributeCount === 0"
                            class="text-muted"
                            >None</span
                          >
                        </td>
                        <td>
                          <span
                            *ngIf="!customList.alreadyExists"
                            class="badge bg-primary"
                            >Will Create</span
                          >
                          <span
                            *ngIf="
                              customList.alreadyExists &&
                              isCustomListSelectedForUpdate(customList.name)
                            "
                            class="badge bg-danger"
                          >
                            <i class="fa fa-sync-alt"></i> Will Replace
                          </span>
                          <span
                            *ngIf="
                              customList.alreadyExists &&
                              !isCustomListSelectedForUpdate(customList.name)
                            "
                            class="badge bg-secondary"
                          >
                            <i class="fa fa-ban"></i> Will Skip
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Roles to Import -->
          <div
            *ngIf="migrationSummary?.roles && migrationSummary.roles.length > 0"
            class="col-12 mb-3"
          >
            <div class="card">
              <div class="card-header bg-dark text-white">
                <h6 class="mb-0">
                  <i class="fa fa-user-shield me-2"></i>
                  Roles to Import
                  <span class="badge bg-white text-dark ms-2">{{
                    migrationSummary.roles.length
                  }}</span>
                </h6>
              </div>
              <div class="card-body">
                <p *ngIf="!hasExistingRoles()" class="mb-3 text-muted">
                  The following roles will be created during the migration:
                </p>
                <div
                  *ngIf="hasExistingRoles()"
                  class="alert alert-warning mb-3"
                >
                  <h6 class="alert-heading">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    Role Conflicts Detected
                  </h6>
                  <p class="mb-2">
                    Some roles from the migration file already exist in your
                    system. By default, existing roles will be skipped to avoid
                    overwriting current role configurations.
                  </p>
                  <p class="mb-0">
                    <strong>Check the box</strong> next to any existing role if
                    you want to <strong>replace it</strong> with the version
                    from the migration file. This will overwrite the existing
                    role configuration.
                  </p>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-hover table-bordered">
                    <thead class="table-light">
                      <tr>
                        <th style="width: 50px;" *ngIf="hasExistingRoles()">
                          Update?
                        </th>
                        <th>Role Name</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        *ngFor="let role of migrationSummary.roles"
                        [style.background-color]="
                          role.alreadyExists &&
                          !isRoleSelectedForUpdate(role.name)
                            ? '#fff3cd'
                            : role.alreadyExists &&
                              isRoleSelectedForUpdate(role.name)
                            ? '#d1e7dd'
                            : 'transparent'
                        "
                      >
                        <td *ngIf="hasExistingRoles()" class="text-center">
                          <input
                            *ngIf="role.alreadyExists"
                            type="checkbox"
                            class="form-check-input"
                            [checked]="isRoleSelectedForUpdate(role.name)"
                            (change)="toggleRoleUpdate(role.name)"
                            title="Check to replace existing role"
                          />
                          <span *ngIf="!role.alreadyExists" class="text-muted"
                            >-</span
                          >
                        </td>
                        <td>
                          <strong>{{ role.name }}</strong>
                          <span
                            *ngIf="role.alreadyExists"
                            class="badge text-white ms-2"
                            style="background-color: #ff9800;"
                          >
                            <i class="fa fa-exclamation-circle"></i> Exists
                          </span>
                        </td>
                        <td>
                          <span
                            *ngIf="!role.alreadyExists"
                            class="badge text-white"
                            style="background-color: #0d6efd;"
                            >Will Create</span
                          >
                          <span
                            *ngIf="
                              role.alreadyExists &&
                              isRoleSelectedForUpdate(role.name)
                            "
                            class="badge text-white"
                            style="background-color: #dc3545;"
                          >
                            <i class="fa fa-sync-alt"></i> Will Replace
                          </span>
                          <span
                            *ngIf="
                              role.alreadyExists &&
                              !isRoleSelectedForUpdate(role.name)
                            "
                            class="badge text-white"
                            style="background-color: #6c757d;"
                          >
                            <i class="fa fa-ban"></i> Will Skip
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Verification and Execute -->
        <div class="row mt-3">
          <div class="col-md-12">
            <!-- Overwrite Workflow Option - Only show if workflow exists -->
            <div
              *ngIf="migrationSummary?.workflowExists"
              class="alert alert-warning mb-3"
            >
              <h6 class="alert-heading">
                <i class="fa fa-exclamation-triangle me-2"></i>
                Workflow Already Exists
              </h6>
              <p class="mb-2">
                A workflow with the name "{{ workflowName }}" already exists.
              </p>
              <div class="switch mb-2">
                <label>
                  <input
                    type="checkbox"
                    id="overwriteWorkflow"
                    [(ngModel)]="overwriteWorkflow"
                  />
                  <strong>Overwrite existing workflow</strong>
                </label>
              </div>
              <small class="text-muted">
                If checked, the existing workflow will be replaced. Otherwise,
                the migration will fail.
              </small>
            </div>

            <div class="switch mb-3">
              <label>
                <input
                  type="checkbox"
                  id="verifySummary"
                  [(ngModel)]="summaryVerified"
                  [disabled]="hasErrors()"
                />
                <strong
                  >I have reviewed the summary and want to proceed with the
                  migration</strong
                >
              </label>
            </div>

            <div *ngIf="hasErrors()" class="alert alert-danger">
              <i class="fa fa-exclamation-triangle me-2"></i>
              Cannot proceed with migration due to errors. Please fix the errors
              in your CSV file and try again.
            </div>

            <button
              class="btn btn-success btn-lg me-2"
              (click)="executeMigration()"
              [disabled]="!canExecuteMigration() || hasErrors()"
            >
              <i class="fa fa-check-circle me-1"></i>
              Execute Migration
            </button>
            <button
              class="btn btn-outline-secondary"
              (click)="reset()"
              [disabled]="loading"
            >
              <i class="fa fa-times me-1"></i>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- End Migration Form Section -->
</div>
<!-- End container-fluid -->
